import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useFilters,
} from "react-table";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import {
  getAllNovedades,
  insertNovedad,
  updateNovedad,
  deleteNovedad,
  uploadImageAppCliente,
} from "../../../api/appClientesApi";
import "./appclientes.css";

export default function Novedades() {
  const [data, setData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef();

  // 🔹 Límites y configuraciones
  const LIMITS = {
    titulo: 60,
    descripcion: 140,
  };
  const IMAGE = {
    maxSizeMB: 1, // ✅ 1 MB permitido
    allowedTypes: ["image/jpeg", "image/png", "image/webp"],
  };

  const [formData, setFormData] = useState({
    titulo: "",
    descripcion: "",
    link: "",
    imagenurl: "",
  });

  const loadNovedades = async () => {
    try {
      setLoading(true);
      const res = await getAllNovedades();
      setData(res || []);
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNovedades();
  }, []);

  // 🔹 Validar y guardar novedad
  const handleSave = async () => {
  try {
    // 🔹 Validaciones de límite (pero no obligatorio)
    if (formData.titulo.length > LIMITS.titulo) {
      return Swal.fire(
        "Límite superado",
        `El título no puede superar ${LIMITS.titulo} caracteres.`,
        "warning"
      );
    }

    if (formData.descripcion.length > LIMITS.descripcion) {
      return Swal.fire(
        "Límite superado",
        `La descripción no puede superar ${LIMITS.descripcion} caracteres.`,
        "warning"
      );
    }

    const file = fileInputRef.current?.files?.[0];
    if (file) {
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > IMAGE.maxSizeMB) {
        return Swal.fire(
          "Imagen demasiado grande",
          `El archivo debe pesar menos de ${IMAGE.maxSizeMB} MB.`,
          "error"
        );
      }
      if (!IMAGE.allowedTypes.includes(file.type)) {
        return Swal.fire(
          "Formato inválido",
          "Solo se permiten imágenes JPG, PNG o WEBP.",
          "error"
        );
      }
    }

    Swal.fire({
      title: "Guardando...",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    let imagenurl = formData.imagenurl;
    if (file) {
      const uploadRes = await uploadImageAppCliente(file);
      imagenurl = uploadRes.filename;
    }

    const payload = {
      titulo: formData.titulo.trim(),   // 👉 ahora permite vacío
      descripcion: formData.descripcion.trim(),
      link: formData.link?.trim() || "",
      imagenurl,
    };

    if (selectedItem) {
      await updateNovedad({ ...payload, idnovedad: selectedItem.idnovedad });
      Swal.fire("Actualizado", "Novedad actualizada correctamente", "success");
    } else {
      await insertNovedad(payload);
      Swal.fire("Agregado", "Novedad agregada correctamente", "success");
    }

    setShowModal(false);
    setSelectedItem(null);
    setImagePreview(null);
    setFormData({ titulo: "", descripcion: "", link: "", imagenurl: "" });
    loadNovedades();

  } catch (err) {
    Swal.fire("Error", err.message, "error");
  }
};


  // 🔹 Eliminar novedad
  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "¿Eliminar novedad?",
      text: "Esta acción no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (confirm.isConfirmed) {
      try {
        await deleteNovedad(id);
        Swal.fire("Eliminado", "Novedad eliminada correctamente", "success");
        loadNovedades();
      } catch (err) {
        Swal.fire("Error", err.message, "error");
      }
    }
  };

  // 🔹 Tabla
  const columns = useMemo(
    () => [
      { Header: "ID", accessor: "idnovedad", className: "text-center", width: 60 },
      { Header: "Título", accessor: "titulo" },
      { Header: "Descripción", accessor: "descripcion" },
      {
        Header: "Link",
        accessor: "link",
        Cell: ({ value }) =>
          value ? (
            <a href={value} target="_blank" rel="noreferrer">
              {value}
            </a>
          ) : (
            "-"
          ),
      },
      {
        Header: "Imagen",
        accessor: "imagenurl",
        Cell: ({ value }) =>
          value ? (
            <img
              src={`https://eethsa.xyz/eethsa1/Uploads/AppClientes/${value}`}
              alt="preview"
              style={{ width: 80, borderRadius: 8 }}
            />
          ) : (
            "-"
          ),
      },
      {
        Header: "Opciones",
        accessor: "acciones",
        Cell: ({ row }) => (
          <div className="text-center">
            <button
              className="btn btn-outline-primary btn-sm me-1"
              onClick={() => {
                setSelectedItem(row.original);
                setImagePreview(
                  row.original.imagenurl
                    ? `https://eethsa.xyz/eethsa1/Uploads/AppClientes/${row.original.imagenurl}`
                    : null
                );
                setShowModal(true);
              }}
            >
              <i className="fas fa-pen"></i>
            </button>
            <button
              className="btn btn-outline-danger btn-sm"
              onClick={() => handleDelete(row.original.idnovedad)}
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const table = useTable(
    { columns, data, initialState: { pageSize: 10 } },
    useFilters,
    useGlobalFilter,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    setGlobalFilter,
    state: { globalFilter },
  } = table;

  // 🔹 Imagen de vista previa
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > IMAGE.maxSizeMB) {
      Swal.fire("Imagen demasiado grande", `Debe ser menor a ${IMAGE.maxSizeMB} MB.`, "error");
      e.target.value = "";
      return;
    }
    if (!IMAGE.allowedTypes.includes(file.type)) {
      Swal.fire("Formato inválido", "Solo se permiten JPG, PNG o WEBP.", "error");
      e.target.value = "";
      return;
    }

    setImagePreview(URL.createObjectURL(file));
  };

  useEffect(() => {
    if (selectedItem) setFormData(selectedItem);
    else setFormData({ titulo: "", descripcion: "", link: "" });
  }, [selectedItem]);

  // 🔹 Render principal
  return (
    <div className="mt-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <button
          className="btn btn-primary"
          onClick={() => {
            setSelectedItem(null);
            setImagePreview(null);
            setShowModal(true);
          }}
        >
          + Agregar Novedad
        </button>
        <input
          type="text"
          placeholder="Buscar..."
          value={globalFilter || ""}
          className="form-control"
          style={{ width: 250 }}
          onChange={(e) => setGlobalFilter(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="loading">Cargando...</div>
      ) : (
        <div className="tabla-scroll">
          <table {...getTableProps()} className="table table-sm tabla-items">
            <thead>
              {headerGroups.map((hg) => (
                <tr {...hg.getHeaderGroupProps()}>
                  {hg.headers.map((col) => (
                    <th {...col.getHeaderProps()}>{col.render("Header")}</th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 🔹 Modal agregar/editar */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedItem ? "Editar" : "Nueva"} Novedad</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Título</Form.Label>
              <Form.Control
                value={formData.titulo}
                maxLength={LIMITS.titulo}
                onChange={(e) =>
                  setFormData({ ...formData, titulo: e.target.value })
                }
              />
              <small className="text-muted">
                {formData.titulo.length}/{LIMITS.titulo} caracteres
              </small>
            </Form.Group>

            <Form.Group className="mt-2">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                maxLength={LIMITS.descripcion}
                value={formData.descripcion}
                onChange={(e) =>
                  setFormData({ ...formData, descripcion: e.target.value })
                }
              />
              <small className="text-muted">
                {formData.descripcion.length}/{LIMITS.descripcion} caracteres
              </small>
            </Form.Group>

            <Form.Group className="mt-2">
              <Form.Label>Link</Form.Label>
              <Form.Control
                value={formData.link}
                onChange={(e) =>
                  setFormData({ ...formData, link: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mt-3">
              <Form.Label>Imagen (máx. 1 MB)</Form.Label>
              <Form.Control
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleImageChange}
              />
              {imagePreview && (
                <img
                  src={imagePreview}
                  alt="preview"
                  className="img-preview mt-2"
                  style={{ maxWidth: "100%", borderRadius: 8 }}
                />
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={() => setShowModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Guardar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
